import { useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PasswordInput } from '../../components/common/PasswordInput';
import { CaptchaWidget, type CaptchaWidgetHandle } from '../../components/common/CaptchaWidget';
import { usePlatformCaptchaConfig } from '../../hooks/useCaptchaConfig';
import { Button } from '../../ui';
import './auth.css';

export function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<CaptchaWidgetHandle | null>(null);

  const { register, error, clearError } = useAuth();
  const { config: captchaConfig, shouldChallenge } = usePlatformCaptchaConfig();
  const captchaRequired = shouldChallenge('admin_register');
  const navigate = useNavigate();

  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!name.trim()) {
      errors.push('Name is required');
    }

    if (!email.trim()) {
      errors.push('Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push('Please enter a valid email address');
    }

    if (!password) {
      errors.push('Password is required');
    } else if (password.length < 8) {
      errors.push('Password must be at least 8 characters');
    }

    if (password !== passwordConfirmation) {
      errors.push('Passwords do not match');
    }

    return errors;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormErrors([]);
    clearError();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    if (captchaRequired && !captchaToken) {
      setFormErrors(['Please complete the CAPTCHA challenge.']);
      return;
    }

    setIsSubmitting(true);
    try {
      await register({
        name,
        email,
        password,
        password_confirmation: passwordConfirmation,
        captcha_token: captchaToken ?? undefined,
      });
      navigate('/', { replace: true });
    } catch {
      // Error is handled by AuthContext
      captchaRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayErrors = formErrors.length > 0 ? formErrors : error ? [error] : [];

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Create Account</h1>
        <p className="auth-subtitle">Sign up to get started with Pryma Auth.</p>

        <form onSubmit={handleSubmit} className="auth-form">
          {displayErrors.length > 0 && (
            <div className="auth-error">
              <ul>
                {displayErrors.map((err, index) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Your name"
              autoComplete="name"
              disabled={isSubmitting}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={isSubmitting}
            />
          </div>

          <PasswordInput
            id="password"
            label="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            disabled={isSubmitting}
          />

          <PasswordInput
            id="passwordConfirmation"
            label="Confirm Password"
            value={passwordConfirmation}
            onChange={e => setPasswordConfirmation(e.target.value)}
            placeholder="Confirm your password"
            autoComplete="new-password"
            disabled={isSubmitting}
          />

          {captchaRequired && captchaConfig?.provider && captchaConfig.siteKey && (
            <CaptchaWidget
              ref={captchaRef}
              provider={captchaConfig.provider}
              siteKey={captchaConfig.siteKey}
              action="admin_register"
              onVerify={setCaptchaToken}
              onError={() => setCaptchaToken(null)}
              onExpire={() => setCaptchaToken(null)}
            />
          )}

          <Button type="submit" block size="lg" loading={isSubmitting}>
            {isSubmitting ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>

        <div className="auth-footer">
          <span>
            Already have an account?{' '}
            <Link to="/login" className="auth-link">
              Sign in
            </Link>
          </span>
        </div>
      </div>
    </div>
  );
}
